import { ReactNode } from 'react';
import Chip from '@material-ui/core/Chip';

type TagPropsGetter = (params: { index: number }) => {
  key?: string | number;
  className?: string;
  disabled?: boolean;
  'data-tag-index'?: number;
  tabIndex?: number;
  onDelete?: (event: any) => void;
};

const labelForOption = (option: unknown): string => {
  if (typeof option === 'string') {
    return option;
  }
  if (option && typeof option === 'object') {
    // Kind picker options are `{ label, value }`.
    const labeled = option as { label?: unknown };
    if (typeof labeled.label === 'string' && labeled.label.length > 0) {
      return labeled.label;
    }
    const entity = option as {
      kind?: string;
      metadata?: { name?: string; namespace?: string };
    };
    const name = entity.metadata?.name;
    if (name) {
      const kind = entity.kind?.toLocaleLowerCase('en-US');
      const namespace = entity.metadata?.namespace;
      if (kind) {
        return namespace && namespace !== 'default'
          ? `${kind}:${namespace}/${name}`
          : `${kind}:${name}`;
      }
      return name;
    }
  }
  return String(option);
};

/**
 * Catalog multi-selects already show selection as checks in the dropdown.
 * The field shows at most one chip so the filter row stays a single line;
 * remaining selections are summarized as "and N more".
 *
 * The chip is the *last* selected value (not the first): Autocomplete's
 * Backspace and the chip's X both remove the last entry, so the visible chip
 * always matches what those actions clear.
 */
export const renderCatalogFilterTags = (
  value: unknown[],
  getTagProps: TagPropsGetter,
): ReactNode => {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }

  const lastIndex = value.length - 1;
  const { key, ...lastTagProps } = getTagProps({ index: lastIndex });
  const more = value.length - 1;

  return [
    <Chip
      size="small"
      label={labelForOption(value[lastIndex])}
      {...lastTagProps}
      key={key ?? lastIndex}
    />,
    ...(more > 0
      ? [
          <span key="more" className="catalog-filter-selection-count">
            {`and ${more} more`}
          </span>,
        ]
      : []),
  ];
};
